/**
 * @voltxampere/web — Built-in shortcut catalog (Sprint 11).
 *
 * Registers global accelerators (Cmd+K, ?, Escape) plus one
 * arm-shortcut per Sprint 10.1 symbol. Idempotent — calling
 * `registerBuiltinShortcuts` twice replaces the existing
 * registrations rather than duplicating.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { ALL_SYMBOLS } from "@voltxampere/ui";

import { armedSymbol, commandPaletteOpen, shortcutOverlayOpen } from "../store/ui.js";
import { currentLocale } from "../store/locale.js";
import { PANE_SHORTCUT_MAP, setPane } from "../store/router.js";
import { toggleWaveformPane } from "../store/waveform-visibility.js";
import { t, type TranslationKey } from "../i18n/index.js";
import { parseSymbolShortcut, shortcuts } from "./index.js";

const PANE_SHORTCUT_DESCRIBE: Readonly<Record<string, TranslationKey>> = {
  "1": "shortcut.pane.landing",
  "2": "shortcut.pane.simulator",
  "3": "shortcut.pane.presets",
  "4": "shortcut.pane.inspector",
  "5": "shortcut.pane.settings",
};

export function registerBuiltinShortcuts(): void {
  // Wipe first so reregistration during HMR is clean.
  shortcuts.clear();

  shortcuts.register({
    key: "Cmd+K",
    scope: "any",
    category: "global",
    handler: () => {
      commandPaletteOpen.value = !commandPaletteOpen.value;
      // Closing the palette implicitly also closes the overlay.
      if (commandPaletteOpen.value) {
        shortcutOverlayOpen.value = false;
      }
    },
    describe: () => t("shortcut.palette.toggle"),
  });

  // Sprint 23 I1 — Cmd+S manual save shortcut. Dispatches a custom
  // event the topbar listens for; the topbar keeps the save flow
  // (putCircuit + status signal) in one place rather than scattered.
  shortcuts.register({
    key: "Cmd+S",
    scope: "any",
    category: "global",
    handler: () => {
      window.dispatchEvent(new CustomEvent("vxa-manual-save"));
    },
    describe: () => t("shortcut.file.save"),
  });

  shortcuts.register({
    key: "?",
    scope: "any",
    category: "global",
    handler: () => {
      shortcutOverlayOpen.value = !shortcutOverlayOpen.value;
      if (shortcutOverlayOpen.value) {
        commandPaletteOpen.value = false;
      }
    },
    describe: () => t("shortcut.shortcuts.toggle"),
  });

  // Sprint 80.Y L-A-2 — Cmd+Shift+O = osiloskop gizle/göster
  // (Şefim #2 closure). Persistent keyboard fallback for the
  // topbar chip; same action as the palette command
  // view.toggle-waveform-pane. The "O" mnemonic — Osiloskop / Oscilloscope.
  shortcuts.register({
    key: "Cmd+Shift+O",
    scope: "global",
    category: "global",
    handler: () => {
      toggleWaveformPane();
    },
    describe: () => t("shortcut.view.toggleWaveformPane"),
  });

  shortcuts.register({
    key: "Escape",
    scope: "modal",
    category: "global",
    handler: () => {
      commandPaletteOpen.value = false;
      shortcutOverlayOpen.value = false;
    },
    describe: () => t("shortcut.modal.close"),
  });

  // Sprint 18 Paket B B1 — 1-5 pane shortcuts. Mockup
  // zenit-platform.html'de demo script'i window-level
  // `keydown` ile aynı 1→landing, 2→simulator, 3→presets,
  // 4→inspector, 5→settings eşlemesini kullanır. ShortcutManager
  // editable-target guard'ı tipik bir form alanındayken sayı
  // tuşlarının pane swap'a kayıp kullanıcının değer girmesini
  // bozmasını engeller. LED sembolünün shortcut'u "7" olduğu
  // için 1-5 ile çakışma yok.
  for (const [key, pane] of Object.entries(PANE_SHORTCUT_MAP)) {
    const descKey = PANE_SHORTCUT_DESCRIBE[key];
    shortcuts.register({
      key,
      scope: "global",
      category: "global",
      handler: () => {
        setPane(pane);
      },
      describe: () => (descKey ? t(descKey) : ""),
    });
  }

  // 20+ symbol arm-shortcuts derived from Sprint 10.1 metadata.
  //
  // Sprint 38-1 — variant entries (e.g. 2N3904 → bjt-npn) share the
  // canonical symbol's shortcut (Q for BJT, M for MOSFET, …) so a
  // single key arm always lands on the base symbol; variants are
  // sidebar-drag-only. Filtering by `schemaType === undefined`
  // selects base symbols only — variants intentionally skipped.
  const baseSymbols = ALL_SYMBOLS.filter((s) => s.schemaType === undefined);
  for (const symbol of baseSymbols) {
    const canonical = parseSymbolShortcut(symbol.shortcut);
    shortcuts.register({
      key: canonical,
      scope: "global",
      category: "symbol",
      handler: () => {
        armedSymbol.value = symbol;
        // Sprint 11 only logs; Sprint 13 will hand off to canvas.
        console.info(`[VXA] armed: ${symbol.id} (${canonical})`);
      },
      describe: () => {
        const name = currentLocale.value === "tr" ? symbol.nameTr : symbol.nameEn;
        return t("shortcut.symbol.arm", { name });
      },
    });
  }
}
